import React, { useState, useEffect, useMemo } from 'react';
import { Siswa, Kelas, UserPrivilege } from '../../types/index.js';
import {
  ArrowUpRight,
  Award,
  ShieldCheck,
  History,
  CheckCircle2,
  AlertCircle,
  Users,
  ArrowRight,
  CheckSquare,
  Square,
  Search,
  Loader2,
  GraduationCap,
} from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { AuditLogBrowser } from './AuditLogBrowser.js';
import { UserPrivilegeView } from './UserPrivilegeView.js';
import { BackupRestoreView } from './BackupRestoreView.js';

interface UtilityViewProps {
  activeUtilityType: 'Naik Kelas' | 'Kelulusan' | 'User Privilege' | 'Activity Log' | 'Backup & Restore';
  siswaList: Siswa[];
  kelasList: Kelas[];
  userPrivilegeList: UserPrivilege[];
  onPromoteClass: (fromKelasId: string, toKelasId: string, studentIds?: string[]) => Promise<any>;
  onGraduateClass: (kelasId: string, studentIds?: string[]) => Promise<any>;
  onAddPrivilege: (data: Partial<UserPrivilege>) => Promise<void>;
  onUpdatePrivilege: (id: string, data: Partial<UserPrivilege>) => Promise<void>;
  onDeletePrivilege: (id: string) => Promise<void>;
}

export const UtilityView: React.FC<UtilityViewProps> = ({
  activeUtilityType,
  siswaList,
  kelasList,
  userPrivilegeList,
  onPromoteClass,
  onGraduateClass,
  onAddPrivilege,
  onUpdatePrivilege,
  onDeletePrivilege,
}) => {
  const { showAlert, showConfirm } = useDialog();

  // State pemilihan kelas
  const [fromKelas, setFromKelas] = useState('');
  const [toKelas, setToKelas] = useState('');
  const [graduateKelas, setGraduateKelas] = useState('');

  // State seleksi siswa
  const [selectedPromoteStudentIds, setSelectedPromoteStudentIds] = useState<string[]>([]);
  const [selectedGraduateStudentIds, setSelectedGraduateStudentIds] = useState<string[]>([]);
  const [searchPromote, setSearchPromote] = useState('');
  const [searchGraduate, setSearchGraduate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Inisialisasi default kelas ketika data kelasList tersedia
  useEffect(() => {
    if (kelasList.length > 0) {
      if (!fromKelas || !kelasList.some((k) => k.id === fromKelas)) {
        setFromKelas(kelasList[0].id);
      }
      if (!toKelas || !kelasList.some((k) => k.id === toKelas)) {
        const defaultTo = kelasList.length > 1 ? kelasList[1].id : kelasList[0].id;
        setToKelas(defaultTo);
      }
      if (!graduateKelas || !kelasList.some((k) => k.id === graduateKelas)) {
        const kelas6 = kelasList.find((k) => k.tingkat === 6) || kelasList[kelasList.length - 1];
        setGraduateKelas(kelas6.id);
      }
    }
  }, [kelasList]);

  // Daftar siswa aktif di kelas asal untuk kenaikan kelas
  const activeStudentsInFromClass = useMemo(() => {
    if (!fromKelas) return [];
    return siswaList.filter((s) => s.kelasId === fromKelas && s.status === 'Aktif');
  }, [siswaList, fromKelas]);

  // Update seleksi otomatis saat kelas asal berubah
  useEffect(() => {
    setSelectedPromoteStudentIds(activeStudentsInFromClass.map((s) => s.id));
    setSearchPromote('');
  }, [fromKelas, activeStudentsInFromClass.length]);

  // Daftar siswa aktif di kelas kelulusan
  const activeStudentsInGraduateClass = useMemo(() => {
    if (!graduateKelas) return [];
    return siswaList.filter((s) => s.kelasId === graduateKelas && s.status === 'Aktif');
  }, [siswaList, graduateKelas]);

  // Update seleksi otomatis saat kelas kelulusan berubah
  useEffect(() => {
    setSelectedGraduateStudentIds(activeStudentsInGraduateClass.map((s) => s.id));
    setSearchGraduate('');
  }, [graduateKelas, activeStudentsInGraduateClass.length]);

  // Filter pencarian siswa
  const filteredPromoteStudents = useMemo(() => {
    if (!searchPromote.trim()) return activeStudentsInFromClass;
    const q = searchPromote.toLowerCase();
    return activeStudentsInFromClass.filter(
      (s) => s.nama.toLowerCase().includes(q) || s.nis.toLowerCase().includes(q)
    );
  }, [activeStudentsInFromClass, searchPromote]);

  const filteredGraduateStudents = useMemo(() => {
    if (!searchGraduate.trim()) return activeStudentsInGraduateClass;
    const q = searchGraduate.toLowerCase();
    return activeStudentsInGraduateClass.filter(
      (s) => s.nama.toLowerCase().includes(q) || s.nis.toLowerCase().includes(q)
    );
  }, [activeStudentsInGraduateClass, searchGraduate]);

  // Toggle selection
  const handleTogglePromoteStudent = (id: string) => {
    setSelectedPromoteStudentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleAllPromote = () => {
    if (selectedPromoteStudentIds.length === activeStudentsInFromClass.length) {
      setSelectedPromoteStudentIds([]);
    } else {
      setSelectedPromoteStudentIds(activeStudentsInFromClass.map((s) => s.id));
    }
  };

  const handleToggleGraduateStudent = (id: string) => {
    setSelectedGraduateStudentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleAllGraduate = () => {
    if (selectedGraduateStudentIds.length === activeStudentsInGraduateClass.length) {
      setSelectedGraduateStudentIds([]);
    } else {
      setSelectedGraduateStudentIds(activeStudentsInGraduateClass.map((s) => s.id));
    }
  };

  // Eksekusi Kenaikan Kelas
  const handlePromote = async () => {
    if (!fromKelas || !toKelas) {
      showAlert('Pilih kelas asal dan kelas tujuan terlebih dahulu!', { type: 'warning' });
      return;
    }
    if (fromKelas === toKelas) {
      showAlert('Kelas asal dan kelas tujuan tidak boleh sama!', { type: 'warning' });
      return;
    }
    if (selectedPromoteStudentIds.length === 0) {
      showAlert('Pilih minimal satu siswa yang akan dinaikkan kelas!', { type: 'warning' });
      return;
    }

    const fromK = kelasList.find((k) => k.id === fromKelas);
    const toK = kelasList.find((k) => k.id === toKelas);

    const isAll = selectedPromoteStudentIds.length === activeStudentsInFromClass.length;
    const confirmMsg = isAll
      ? `Konfirmasi menaikkan SEMUA (${selectedPromoteStudentIds.length}) siswa dari kelas "${fromK?.nama}" ke kelas "${toK?.nama}"?`
      : `Konfirmasi menaikkan ${selectedPromoteStudentIds.length} dari ${activeStudentsInFromClass.length} siswa terpilih dari "${fromK?.nama}" ke "${toK?.nama}"? Siswa yang tidak dicentang akan tetap berada di kelas saat ini.`;

    if (await showConfirm(confirmMsg, { title: 'Konfirmasi Kenaikan Kelas' })) {
      try {
        setIsProcessing(true);
        const res = await onPromoteClass(fromKelas, toKelas, selectedPromoteStudentIds);
        showAlert(
          res?.message || `Berhasil menaikkan ${selectedPromoteStudentIds.length} siswa ke kelas ${toK?.nama}!`,
          { type: 'success', title: 'Kenaikan Kelas Berhasil' }
        );
      } catch (err: any) {
        showAlert(err.message || 'Gagal memproses kenaikan kelas', { type: 'danger', title: 'Terjadi Kesalahan' });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // Eksekusi Kelulusan Siswa
  const handleGraduate = async () => {
    if (!graduateKelas) {
      showAlert('Pilih kelas siswa tingkat akhir terlebih dahulu!', { type: 'warning' });
      return;
    }
    if (selectedGraduateStudentIds.length === 0) {
      showAlert('Pilih minimal satu siswa yang akan diluluskan!', { type: 'warning' });
      return;
    }

    const k = kelasList.find((x) => x.id === graduateKelas);
    const confirmMsg = `Konfirmasi menetapkan ${selectedGraduateStudentIds.length} siswa kelas "${k?.nama}" sebagai Alumni (Status LULUS)?`;

    if (await showConfirm(confirmMsg, { title: 'Konfirmasi Kelulusan Siswa' })) {
      try {
        setIsProcessing(true);
        const res = await onGraduateClass(graduateKelas, selectedGraduateStudentIds);
        showAlert(
          res?.message || `Berhasil mengubah status ${selectedGraduateStudentIds.length} siswa menjadi LULUS!`,
          { type: 'success', title: 'Kelulusan Berhasil' }
        );
      } catch (err: any) {
        showAlert(err.message || 'Gagal memproses kelulusan siswa', { type: 'danger', title: 'Terjadi Kesalahan' });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const fromKObj = kelasList.find((k) => k.id === fromKelas);
  const toKObj = kelasList.find((k) => k.id === toKelas);
  const gradKObj = kelasList.find((k) => k.id === graduateKelas);

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900">Utility - {activeUtilityType}</h2>
          <p className="text-xs text-slate-500">
            Alat pemeliharaan data akhir tahun ajaran, kenaikan rombongan belajar, kelulusan, dan hak akses
          </p>
        </div>
      </div>

      {/* ======================= FITUR NAIK KELAS ======================= */}
      {activeUtilityType === 'Naik Kelas' && (
        <div className="space-y-4">
          {/* Card Pemilihan Rombel Asal & Tujuan */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs">
            <div className="flex items-center gap-2 mb-4 text-sky-800">
              <ArrowUpRight className="w-5 h-5 text-sky-600" />
              <h3 className="font-bold text-sm text-slate-900">Konfigurasi Rombongan Belajar (Rombel)</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-center">
              {/* Dropdown Kelas Asal */}
              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Kelas Asal (Rombel Sekarang)
                </label>
                <select
                  value={fromKelas}
                  onChange={(e) => setFromKelas(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white text-xs font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                >
                  {kelasList.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.nama} (Tingkat {k.tingkat}) — {k.jumlahSiswa || 0} Siswa
                    </option>
                  ))}
                </select>
                {fromKObj && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Wali Kelas: <span className="font-semibold text-slate-700">{fromKObj.waliKelas || '-'}</span> | Total:{' '}
                    <span className="font-semibold text-sky-600">{activeStudentsInFromClass.length} Siswa Aktif</span>
                  </p>
                )}
              </div>

              {/* Panah Indikator */}
              <div className="hidden lg:flex justify-center items-center">
                <div className="w-10 h-10 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-600">
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>

              {/* Dropdown Kelas Tujuan */}
              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Kelas Tujuan (Rombel Tahun Ajaran Baru)
                </label>
                <select
                  value={toKelas}
                  onChange={(e) => setToKelas(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white text-xs font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                >
                  {kelasList.map((k) => (
                    <option key={k.id} value={k.id} disabled={k.id === fromKelas}>
                      {k.nama} (Tingkat {k.tingkat}) {k.id === fromKelas ? '— (Kelas Asal)' : `— ${k.jumlahSiswa || 0} Siswa`}
                    </option>
                  ))}
                </select>
                {toKObj && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Tingkat Kelas Baru: <span className="font-semibold text-slate-700">Kelas {toKObj.tingkat}</span> | Wali Kelas:{' '}
                    <span className="font-semibold text-slate-700">{toKObj.waliKelas || '-'}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Daftar Siswa yang Akan Dinaikkan */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-sky-600" />
                <h3 className="font-bold text-sm text-slate-900">
                  Daftar Siswa Kelas {fromKObj?.nama || ''}
                </h3>
                <span className="text-xs bg-sky-100 text-sky-800 font-semibold px-2.5 py-0.5 rounded-full">
                  {selectedPromoteStudentIds.length} dari {activeStudentsInFromClass.length} Dipilih
                </span>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari NIS atau Nama Siswa..."
                    value={searchPromote}
                    onChange={(e) => setSearchPromote(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleToggleAllPromote}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1.5 transition whitespace-nowrap"
                >
                  {selectedPromoteStudentIds.length === activeStudentsInFromClass.length ? (
                    <>
                      <CheckSquare className="w-3.5 h-3.5 text-sky-600" />
                      <span>Batal Pilih Semua</span>
                    </>
                  ) : (
                    <>
                      <Square className="w-3.5 h-3.5 text-slate-400" />
                      <span>Pilih Semua</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Tabel Siswa */}
            <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="py-2.5 px-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={
                          activeStudentsInFromClass.length > 0 &&
                          selectedPromoteStudentIds.length === activeStudentsInFromClass.length
                        }
                        onChange={handleToggleAllPromote}
                        className="rounded text-sky-600 focus:ring-sky-500 w-4 h-4"
                      />
                    </th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">NIS</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">Nama Siswa</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">Angkatan</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">Status Kenaikan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPromoteStudents.map((s) => {
                    const isChecked = selectedPromoteStudentIds.includes(s.id);
                    return (
                      <tr
                        key={s.id}
                        onClick={() => handleTogglePromoteStudent(s.id)}
                        className={`cursor-pointer transition ${
                          isChecked ? 'bg-sky-50/50 hover:bg-sky-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleTogglePromoteStudent(s.id)}
                            className="rounded text-sky-600 focus:ring-sky-500 w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="py-2 px-3 font-mono font-medium text-slate-700">{s.nis}</td>
                        <td className="py-2 px-3 font-semibold text-slate-900">{s.nama}</td>
                        <td className="py-2 px-3 text-slate-500">{s.thnAngkatan || '-'}</td>
                        <td className="py-2 px-3">
                          {isChecked ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              Naik ke {toKObj?.nama || 'Kelas Baru'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                              <AlertCircle className="w-3 h-3 text-amber-600" />
                              Tinggal di Kelas Ini
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPromoteStudents.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-400">
                        {activeStudentsInFromClass.length === 0
                          ? 'Tidak ada siswa aktif di kelas asal ini.'
                          : 'Tidak ada siswa yang cocok dengan kata kunci pencarian.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Action Bar Bawah */}
            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-600">
                Pindahkan <strong className="text-sky-700">{selectedPromoteStudentIds.length}</strong> siswa dari{' '}
                <strong className="text-slate-800">{fromKObj?.nama}</strong> ke{' '}
                <strong className="text-slate-800">{toKObj?.nama}</strong>.
              </div>
              <button
                type="button"
                onClick={handlePromote}
                disabled={isProcessing || selectedPromoteStudentIds.length === 0 || fromKelas === toKelas}
                className="px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-sm transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sedang Memproses Kenaikan...</span>
                  </>
                ) : (
                  <>
                    <ArrowUpRight className="w-4 h-4" />
                    <span>Proses Kenaikan Kelas ({selectedPromoteStudentIds.length} Siswa)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================= FITUR KELULUSAN ======================= */}
      {activeUtilityType === 'Kelulusan' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs">
            <div className="flex items-center gap-2 mb-3 text-amber-700">
              <GraduationCap className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-sm text-slate-900">Pilih Rombel Siswa Tingkat Akhir (Kelas 6)</h3>
            </div>
            <p className="text-xs text-slate-600 mb-4">
              Pilih kelas siswa yang telah menyelesaikan masa pendidikan di SD Muhammadiyah Terpadu Ponorogo untuk ditandai sebagai Alumni (LULUS).
            </p>

            <div className="max-w-md">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Pilih Kelas Tingkat Akhir
              </label>
              <select
                value={graduateKelas}
                onChange={(e) => setGraduateKelas(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:bg-white text-xs font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              >
                {kelasList.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.nama} (Tingkat {k.tingkat}) — {k.jumlahSiswa || 0} Siswa
                  </option>
                ))}
              </select>
              {gradKObj && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Wali Kelas: <span className="font-semibold text-slate-700">{gradKObj.waliKelas || '-'}</span> | Total:{' '}
                  <span className="font-semibold text-amber-600">{activeStudentsInGraduateClass.length} Siswa Aktif</span>
                </p>
              )}
            </div>
          </div>

          {/* Daftar Siswa yang Akan Diluluskan */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                <h3 className="font-bold text-sm text-slate-900">
                  Daftar Calon Alumni Kelas {gradKObj?.nama || ''}
                </h3>
                <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2.5 py-0.5 rounded-full">
                  {selectedGraduateStudentIds.length} dari {activeStudentsInGraduateClass.length} Dipilih
                </span>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari NIS atau Nama Siswa..."
                    value={searchGraduate}
                    onChange={(e) => setSearchGraduate(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleToggleAllGraduate}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1.5 transition whitespace-nowrap"
                >
                  {selectedGraduateStudentIds.length === activeStudentsInGraduateClass.length ? (
                    <>
                      <CheckSquare className="w-3.5 h-3.5 text-amber-600" />
                      <span>Batal Pilih Semua</span>
                    </>
                  ) : (
                    <>
                      <Square className="w-3.5 h-3.5 text-slate-400" />
                      <span>Pilih Semua</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Tabel Siswa Kelulusan */}
            <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="py-2.5 px-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={
                          activeStudentsInGraduateClass.length > 0 &&
                          selectedGraduateStudentIds.length === activeStudentsInGraduateClass.length
                        }
                        onChange={handleToggleAllGraduate}
                        className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4"
                      />
                    </th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">NIS</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">Nama Siswa</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">Angkatan</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-600">Status Akhir</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredGraduateStudents.map((s) => {
                    const isChecked = selectedGraduateStudentIds.includes(s.id);
                    return (
                      <tr
                        key={s.id}
                        onClick={() => handleToggleGraduateStudent(s.id)}
                        className={`cursor-pointer transition ${
                          isChecked ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleGraduateStudent(s.id)}
                            className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="py-2 px-3 font-mono font-medium text-slate-700">{s.nis}</td>
                        <td className="py-2 px-3 font-semibold text-slate-900">{s.nama}</td>
                        <td className="py-2 px-3 text-slate-500">{s.thnAngkatan || '-'}</td>
                        <td className="py-2 px-3">
                          {isChecked ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                              <Award className="w-3 h-3 text-amber-600" />
                              Lulus (Alumni)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                              Tetap Siswa Aktif
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredGraduateStudents.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-400">
                        {activeStudentsInGraduateClass.length === 0
                          ? 'Tidak ada siswa aktif di kelas ini.'
                          : 'Tidak ada siswa yang cocok dengan kata kunci pencarian.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Action Bar Bawah */}
            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-600">
                Tetapkan <strong className="text-amber-700">{selectedGraduateStudentIds.length}</strong> siswa kelas{' '}
                <strong className="text-slate-800">{gradKObj?.nama}</strong> sebagai Alumni.
              </div>
              <button
                type="button"
                onClick={handleGraduate}
                disabled={isProcessing || selectedGraduateStudentIds.length === 0}
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold shadow-sm transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sedang Memproses Kelulusan...</span>
                  </>
                ) : (
                  <>
                    <Award className="w-4 h-4" />
                    <span>Tandai Sebagai Alumni Lulus ({selectedGraduateStudentIds.length} Siswa)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================= FITUR USER PRIVILEGE ======================= */}
      {activeUtilityType === 'User Privilege' && (
        <UserPrivilegeView
          userPrivilegeList={userPrivilegeList}
          onAddPrivilege={onAddPrivilege}
          onUpdatePrivilege={onUpdatePrivilege}
          onDeletePrivilege={onDeletePrivilege}
        />
      )}

      {/* ======================= FITUR ACTIVITY LOG ======================= */}
      {activeUtilityType === 'Activity Log' && <AuditLogBrowser />}

      {/* ======================= FITUR BACKUP & RESTORE ======================= */}
      {activeUtilityType === 'Backup & Restore' && <BackupRestoreView />}
    </div>
  );
};

